import {Types} from "@Common/types";

import {cst, cstAssign}           from "@Common/react/chain-services";
import {consumeContext}           from "@Common/react/context";
import {CsForm, makeFieldCreator} from "@Common/react/cs-form/cs-form";
import {required}                 from "@Common/react/cs-form/validators/required";
import {Load2}                    from "@Common/react/load2";
import {US_STATES}                from "@Common/ui-components/heavy/common/us-states";
import {debouncePromise}          from "@Common/utils/debounce";

import {BRANCH_LEVEL} from "@Xpresso/routes/common/logic/user-level";

type Agent        = Types.Apis.Payloads.Next.Agent.GetAll.Response[any];
type ListingModel = Types.Xpresso.Account.Admin.Listings.ListingModel;

interface VarStack {
    agentListLoad  : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.Agent.GetAllUsers.Response>,
    apis           : Types.Apis.Loaders.XpressoLoadApis,
    branchListLoad : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.Branch.GetAll.Response>,
    csForm         : Types.Common.React.CsForm.CsFormNext<ListingModel>,
    fields         : ListingFormField<keyof ListingModel>[],
    selectOptions  : {[key in keyof ListingModel]?: Array<ListingModel[key]>},
    session        : any,
    userLevels     : Types.Apis.Loaders.LoadUserLevelsNext,
}

export const ListingFormState = ({
    apmSupport = false,
    next,
    value
}:{
    apmSupport ?: boolean,
    next        : (v:Types.Xpresso.Account.Admin.Listings.ListingFormStateNext) => any,
    value      ?: ListingModel,
}) => cst<VarStack>(
    consumeContext("apis"),
    consumeContext("session"),
    consumeContext("userLevels"),
    cstAssign("fields", ({}, next) => next(makeFields({apmSupport}))),
    cstAssign("csForm", ({fields}, next) => CsForm<ListingModel>({next, fields, initValue: value})),
    cstAssign("branchListLoad", ({apis, userLevels}, next) => Load2({
        next,
        fetch: async () => (
            userLevels.userLevel === BRANCH_LEVEL
                ? []
                : apis.next.getBranchList()
        ),
    })),
    cstAssign("agentListLoad", ({apis, csForm}, next) => Load2({
        next,
        _key: csForm.value.branch?.idBranch,
        fetch: async () => (
            csForm.value.branch
                ? apis.next.getAgentList(csForm.value.branch.idBranch)
                : []
        ),
    })),
    cstAssign("selectOptions", (vars, next) => next(makeSelectOptions(vars))),
    ({agentListLoad, branchListLoad, csForm, fields, selectOptions, userLevels}) => {
        const handleFieldInput = <Path extends keyof ListingModel>(path:Path, value:ListingModel[Path]) => {
            csForm.field(path).onChange(value);

            if (path === "branch") {
                csForm.field("agent").onChange(null);
                agentListLoad.reload();
            } else if (path === "agent") {
                csForm.field("agentEmailAddress").onChange((value as Agent)?.agentEmail || "");
            }
        };

        return next({

            fields: fields.reduce((fields, {path, required}) => {
                const field:Types.Xpresso.Account.Admin.Listings.ListingFormContentProps['fields'][typeof path] = {
                    error         : csForm.field(path).getError()?.getMessage(),
                    disabled      : (
                        (path === "branch" && userLevels.userLevel === BRANCH_LEVEL)
                        || (!csForm.value.agent && !["agent", "branch"].includes(path))
                    ),
                    required      : required?.({listing: csForm.value}) ?? false,
                    onInput       : (value:ListingModel[typeof path]) => handleFieldInput(path, value),
                    selectOptions : selectOptions[path] as any,
                    value         : csForm.value[path],
                };
                return {...fields, [path]: field};
            }, {} as Types.Xpresso.Account.Admin.Listings.ListingFormContentProps['fields']),

            selectedAgent: agentListLoad.value?.find(agent => agent.idAgent === csForm.value.agent?.idAgent),
            selectedBranch: branchListLoad.value?.find(branch => branch.idBranch === csForm.value.branch?.idBranch),

            valid: csForm.validate(),

            value: csForm.value,
        })
    },
);

const makeSelectOptions = ({agentListLoad, branchListLoad, session, userLevels}:Omit<VarStack, "selectOptions">): VarStack['selectOptions'] => {
    return {
        agent  : agentListLoad.value,
        branch : userLevels.userLevel === BRANCH_LEVEL && session.data.branch ? [session.data.branch] : branchListLoad.value,

        firePlace     : [true, false],
        listingType   : ["jl", "js"],
        locationState : US_STATES.map(({abbreviation}) => abbreviation),
    };
};

const makeUrlIsImageValidator = () => ({
    check             : memoizeAsync(debouncePromise(async (url:string) => url ? validateUrlIsImage(url) : true, 500)),
    getMessage        : () => "Please use an image URL",
    getPendingMessage : () => "Validating URL...",
});

const memoizeAsync = <Args extends any[], AwaitedReturn>(fn: (...args:Args) => Promise<AwaitedReturn>): (...args:Args) => Promise<AwaitedReturn> => {
    const cache = new Map<string, Promise<AwaitedReturn>>();
    return async (...args:Args): Promise<AwaitedReturn> => {
        const serialized = JSON.stringify(args);
        if (cache.has(serialized)) {
            return cache.get(serialized);
        }

        const result = await fn(...args)
        cache.set(serialized, Promise.resolve(result));
        return result;
    };
};

const positiveNumberValidator:Types.Common.React.CsForm.Validator<ListingModel, "listedPrice" | "soldPrice"> = {
    check: (fieldValue) => !isNaN(fieldValue) && fieldValue >= 0,
    getMessage: () => "Please enter a positive number",
};

const requiredForJustListedValidator:Types.Common.React.CsForm.Validator<ListingModel, keyof ListingModel> = {
    check: (fieldValue, {data: listing}) => listing.listingType !== "jl" || Boolean(fieldValue),
    getMessage: () => "This field is required for Just Listed",
};

const requiredForJustSoldValidator:Types.Common.React.CsForm.Validator<ListingModel, keyof ListingModel> = {
    check: (fieldValue, {data: listing}) => listing.listingType !== "js" || Boolean(fieldValue),
    getMessage: () => "This field is required for Just Sold",
};

const requiredForOpenHouse:Types.Common.React.CsForm.Validator<ListingModel, "openHouseDate" | "openHouseEndTime" | "openHouseStartTime"> = {
    check: (fieldValue, {data: listing}) => (
        fieldValue != null
        || !openHouseRequiredFilter({listing})
    ),
    getMessage: () => "Required for Open House",
};

interface ListingFormField<Path extends keyof ListingModel & string> extends Types.Common.React.CsForm.Field<ListingModel, Path> {
    required?: (context:{listing: ListingModel}) => boolean,
}
type RequiredFilter = ListingFormField<any>['required'];

const alwaysRequiredFilter:RequiredFilter     = () => true;
const justListedRequiredFilter:RequiredFilter = ({listing}) => listing.listingType === "jl";
const justSoldRequiredFilter:RequiredFilter   = ({listing}) => listing.listingType === "js";
const openHouseRequiredFilter:RequiredFilter  = ({listing}) => [listing.openHouseDate, listing.openHouseStartTime, listing.openHouseEndTime].some(v => v != null);

const createField = makeFieldCreator<ListingModel>();
const makeFields = ({apmSupport}:{apmSupport: boolean}): ListingFormField<keyof ListingModel>[] => [
    createField({path: "agent",                 defaultValue: null, validators: [required],                       required: alwaysRequiredFilter}),
    createField({path: "branch",                defaultValue: null, validators: [required],                       required: alwaysRequiredFilter}),
    createField({path: "agentEmailAddress",     defaultValue: "",   validators: [required],                       required: alwaysRequiredFilter}),
    createField({path: "bathrooms",             defaultValue: 1}),
    createField({path: "bedrooms",              defaultValue: 1}),
    createField({path: "dateListed",            defaultValue: null, validators: [required],                       required: alwaysRequiredFilter}),
    createField({path: "dateSold",              defaultValue: null, validators: [requiredForJustSoldValidator],   required: justSoldRequiredFilter}),
    createField({path: "description",           defaultValue: ""}),
    createField({path: "eventNotifyAgent",      defaultValue: false}),
    createField({path: "firePlace",             defaultValue: false}),
    createField({
        path         : "listedPrice",
        defaultValue : null,
        validators   : [requiredForJustListedValidator, positiveNumberValidator],
        required     : justListedRequiredFilter,
    }),
    createField({path: "listingUrl",            defaultValue: ""}),
    createField({path: "listingType",           defaultValue: "jl"}),
    createField({path: "locationCity",          defaultValue: "",   validators: [required],                       required: alwaysRequiredFilter}),
    createField({path: "locationState",         defaultValue: "",   validators: [required],                       required: alwaysRequiredFilter}),
    createField({path: "locationStreetAddress", defaultValue: "",   validators: [required],                       required: alwaysRequiredFilter}),
    createField({path: "locationUnitNumber",    defaultValue: ""}),
    createField({path: "locationZip",           defaultValue: "",   validators: [required],                       required: alwaysRequiredFilter}),
    createField({path: "lotSize",               defaultValue: ""}),
    createField({path: "lotSizeSquareFeet",     defaultValue: 0}),
    createField({path: "livingArea",            defaultValue: 0}),
    createField({path: "mlsId",                 defaultValue: "",   validators: [required],                       required: alwaysRequiredFilter}),
    createField({path: "mlsName",               defaultValue: "",   validators: [required],                       required: alwaysRequiredFilter}),
    createField({path: "openHouseDate",         defaultValue: null, validators: [requiredForOpenHouse],           required: openHouseRequiredFilter}),
    createField({path: "openHouseStartTime",    defaultValue: null, validators: [requiredForOpenHouse],           required: openHouseRequiredFilter}),
    createField({path: "openHouseEndTime",      defaultValue: null, validators: [requiredForOpenHouse],           required: openHouseRequiredFilter}),
    createField({
        path         : "picture0",
        defaultValue : "",
        validators   : [...apmSupport ? [required] : [], makeUrlIsImageValidator()],
        required     : apmSupport ? alwaysRequiredFilter : null,
    }),
    createField({path: "picture1",              defaultValue: "",   validators: [makeUrlIsImageValidator()]}),
    createField({path: "picture2",              defaultValue: "",   validators: [makeUrlIsImageValidator()]}),
    createField({path: "picture3",              defaultValue: "",   validators: [makeUrlIsImageValidator()]}),
    createField({path: "picture4",              defaultValue: "",   validators: [makeUrlIsImageValidator()]}),
    createField({path: "picture5",              defaultValue: "",   validators: [makeUrlIsImageValidator()]}),
    createField({path: "picture6",              defaultValue: "",   validators: [makeUrlIsImageValidator()]}),
    createField({path: "picture7",              defaultValue: "",   validators: [makeUrlIsImageValidator()]}),
    createField({path: "picture8",              defaultValue: "",   validators: [makeUrlIsImageValidator()]}),
    createField({path: "picture9",              defaultValue: "",   validators: [makeUrlIsImageValidator()]}),
    createField({path: "primaryBuyerFirstName", defaultValue: ""}),
    createField({path: "primaryBuyerLastName",  defaultValue: ""}),
    createField({path: "propertyType",          defaultValue: ""}),
    createField({path: "roomCount",             defaultValue: 1}),
    createField({
        path: "soldPrice",
        defaultValue: null,
        validators: [requiredForJustSoldValidator, positiveNumberValidator],
        required: justSoldRequiredFilter,
    }),
    createField({path: "title",                 defaultValue: ""}),
    createField({path: "virtualTourUrl",        defaultValue: ""}),
    createField({path: "yearBuilt",             defaultValue: ""}),
];

const validateUrlIsImage = (url:string): Promise<boolean> => {
    try {
        // throw on bad URLs
        new URL(url);

        return new Promise(resolve => {
            // try loading the image with an img tag because CORS will block a fetch
            const img = new Image();
            img.addEventListener("load", () => resolve(true));
            img.addEventListener("error", () => resolve(false));
            img.src = url;
        });
    } catch (error) {
        return Promise.resolve(false);
    }
};

